import type { Team } from '@compass/client/types/team';

import { useAllTasksAccessMode } from '@/hooks/useShouldShowAllTasks';
import { useWorkforceEligiblePrograms } from '@/hooks/useShouldShowWorkforceConsole';
import { useMyManagedTeams } from '@/pages/workforce/hooks/useMyManagedTeams';
import { useWorkforceTeams } from '@/pages/workforce/hooks/useWorkforceTeams';
import { useStore } from '@/store/store';

/**
 * The teams All Tasks uses for its assignee filter and Team column, from the
 * same source the Workforce Console uses under the All Tasks access mode:
 * the teams the user leads (flag off), or every team linked to a program where
 * the user may use the console (flag on). The query keys are shared with the
 * console, so neither page asks twice.
 */
export const useAllTasksTeams = (): { teams: Team[]; isLoading: boolean } => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const mode = useAllTasksAccessMode();
  const leads = useMyManagedTeams({ enabled: !mode.isLoading && !mode.permissionsBased });
  const eligible = useWorkforceEligiblePrograms(mode.permissionsBased);
  const linked = useWorkforceTeams(organizationId, eligible.programs);

  if (mode.isLoading) return { teams: [], isLoading: true };
  if (!mode.permissionsBased) return { teams: leads.managedTeams, isLoading: leads.isLoading };
  return { teams: linked.teams, isLoading: eligible.isLoading || linked.isLoading };
};

/** The team a user belongs to (member or lead). A user is on at most one team per org. */
export const teamOfUser = (teams: readonly Team[], userId: string | undefined): Team | undefined =>
  userId
    ? teams.find(team => team.manager_id === userId || team.member_ids?.includes(userId))
    : undefined;
